import type { DoneInvokeEvent } from 'xstate';
import { assign, createMachine } from 'xstate';
import type { SubscriptionInfo } from '~/subscription-management/api/subscription/model';
import { getSubscriptionInfo } from '~/subscription-management/api/subscription/get-subscription-info';
import { getAllInvoices } from '../api/invoices';
import type { Invoice } from '../api/invoices';
import type {
  Context,
  SetCustomerDetailsEvent,
  Event,
  CancelEvent,
  UpdateBillingNotificationsEvent,
} from './invoices-page.machine.types';
import { subscriptionApi } from '../api';
import { FORM_NAME, trackFormSubmit } from '../utils/tracking';
import type { Currency, Customer } from '../api/common/model/customer';

export const invoicesPageMachine = createMachine(
  {
    // eslint-disable-next-line @typescript-eslint/consistent-type-imports
    tsTypes: {} as import('./invoices.machine.typegen').Typegen0,
    schema: {
      context: {} as Context,
      events: {} as Event,
      services: {} as {
        getSubscriptionInfo: { data: SubscriptionInfo };
        loadInvoices: { data: Invoice[] };
        updateCustomerDetailsService: { data: SubscriptionInfo };
      },
    },
    id: 'invoices-page',
    type: 'parallel',
    states: {
      details: {
        initial: 'initialise',
        states: {
          initialise: {
            id: 'initialise',
            invoke: {
              src: 'getSubscriptionInfo',
              id: 'getSubscriptionInfo',
              onDone: [
                {
                  cond: 'customerExists',

                  actions: assign<Context, DoneInvokeEvent<SubscriptionInfo>>(
                    (_context, event) => ({
                      accountId: event.data.user.accountId,
                      userId: event.data.user.userId,
                      customer: event.data.customer,
                      currency:
                        event.data.customer?.currency ??
                        (event.data.user.currency.toUpperCase() as Currency) ??
                        'EUR',
                    }),
                  ),

                  target: 'viewInvoiceDetails',
                },
                {
                  actions: assign<Context, DoneInvokeEvent<SubscriptionInfo>>(
                    (_context, event) => ({
                      accountId: event.data.user.accountId,
                    }),
                  ),
                  target: 'noInvoiceDetails',
                },
              ],
              onError: [
                {
                  target: 'noInvoiceDetails',
                },
              ],
            },
          },
          noInvoiceDetails: {},
          viewInvoiceDetails: {
            type: 'parallel',
            states: {
              address: {
                initial: 'initial',
                states: {
                  initial: {
                    always: [
                      {
                        cond: (context) => context.startInInvoiceEditMode === true,
                        target: 'editing',
                      },
                      { target: 'viewing' },
                    ],
                  },
                  viewing: {
                    id: 'viewing',
                    on: {
                      EDIT_ADDRESS: {
                        target: 'editing',
                      },
                    },
                  },
                  editing: {
                    id: 'editing',
                    on: {
                      UPDATE_ADDRESS: {
                        actions: assign<Context, SetCustomerDetailsEvent>({
                          hasAddressError: false,
                        }),
                        target: '#updateAddress',
                      },
                      CANCEL_ADDRESS_UPDATE: {
                        target: 'viewing',
                        actions: assign<Context, CancelEvent>(() => ({
                          hasAddressError: false,
                        })),
                      },
                    },
                  },
                  update: {
                    id: 'updateAddress',
                    invoke: {
                      id: 'updateCustomerDetailsService',
                      src: 'updateCustomerDetailsService',
                      onDone: [
                        {
                          target: 'viewing',
                          actions: [
                            assign<Context, DoneInvokeEvent<SubscriptionInfo>>((_, { data }) => ({
                              hasAddressError: false,
                              customer: data.customer,
                            })),
                            (context) =>
                              trackFormSubmit({
                                name: FORM_NAME.updateInvoiceDetails,
                                submitSuccess: true,
                                accountId: context.accountId,
                              }),
                          ],
                        },
                      ],
                      onError: [
                        {
                          target: 'editing',
                          actions: [
                            assign<Context, DoneInvokeEvent<SubscriptionInfo>>(() => ({
                              hasAddressError: true,
                            })),
                            (context, event) =>
                              trackFormSubmit({
                                name: FORM_NAME.updateInvoiceDetails,
                                submitSuccess: false,
                                accountId: context.accountId,
                                errorMessage: event.data?.message,
                              }),
                          ],
                        },
                      ],
                    },
                  },
                },
              },
              notifications: {
                on: {
                  UPDATE_BILLING_NOTIFICATIONS: {
                    actions: assign<Context, UpdateBillingNotificationsEvent>({
                      customer: (context, event) => ({
                        ...(context.customer as Customer),
                        additionalEmailAddresses: event.emails,
                      }),
                    }),
                  },
                },
              },
            },
          },
        },
      },
      invoices: {
        initial: 'initialise',
        states: {
          initialise: {
            invoke: {
              src: 'loadInvoices',
              id: 'loadInvoices',
              onDone: [
                {
                  target: 'viewInvoices',
                  actions: assign<Context, DoneInvokeEvent<Invoice[]>>((_context, event) => ({
                    invoices: event.data,
                  })),
                  cond: 'hasInvoices',
                },
                {
                  target: 'noInvoices',
                },
              ],
              onError: {
                target: 'noInvoices',
              },
            },
          },
          viewInvoices: {},
          noInvoices: {},
        },
      },
    },
  },
  {
    services: {
      getSubscriptionInfo,
      updateCustomerDetailsService: (_, { newCustomer }): Promise<SubscriptionInfo> => {
        return subscriptionApi
          .updateCustomerDetails({ ...newCustomer })
          .then(() => getSubscriptionInfo());
      },
      loadInvoices: getAllInvoices,
    },
    guards: {
      customerExists: (_context, event: DoneInvokeEvent<SubscriptionInfo>) =>
        event.data.customer !== null,
      hasInvoices: (_, event: DoneInvokeEvent<Invoice[]>) => event.data.length > 0,
    },
  },
);
